import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, FileText, Settings, Shield, Zap } from "lucide-react";

export default function DashboardRightPanel() {
  const [activeStep, setActiveStep] = useState(1);
  const [completed, setCompleted] = useState([]);
  const [questionnaireVisible, setQuestionnaireVisible] = useState(false);
  const [answers, setAnswers] = useState({});

  const handleStepClick = (step) => {
    // When clicking the first step, toggle questionnaire visibility
    if (step === 1) {
      setQuestionnaireVisible(true);
    } else {
      setQuestionnaireVisible(false);
    }

    // Proceed step logic
    if (step <= activeStep + 1) {
      setActiveStep(step);
      if (!completed.includes(step - 1) && step > 1) {
        setCompleted([...completed, step - 1]);
      }
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers({ ...answers, [field]: value });
  };

  const handleSubmitQuestionnaire = () => {
    console.log("✅ Submitted answers:", answers);
    setQuestionnaireVisible(false);
    setCompleted([...completed, 1]);
    setActiveStep(2);
  };

  const steps = [
    { id: 1, title: "Complete Questionnaire", icon: <FileText size={22} /> },
    { id: 2, title: "Set Career Preferences", icon: <Settings size={22} /> },
    { id: 3, title: "Consent Terms & Policy", icon: <Shield size={22} /> },
    { id: 4, title: "Unlock AI Results ⚡", icon: <Zap size={22} /> },
  ];

  return (
    <div className="relative bg-gradient-to-b from-[#1b0b1b] to-[#0d0d1a] border border-[#EB6AB5]/20 rounded-3xl p-8 w-[360px] shadow-2xl overflow-hidden">
      {/* Dotted Line */}
      <div className="absolute left-10 top-20 bottom-12 border-l border-dotted border-[#EB6AB5]/40" />

      <div className="space-y-12 relative z-10">
        {steps.map((step, i) => (
          <div key={step.id} className="flex flex-col gap-3">
            <div
              onClick={() => handleStepClick(step.id)}
              className="flex items-start gap-4 cursor-pointer group"
            >
              {/* Icon */}
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 ${
                  completed.includes(step.id)
                    ? "bg-green-500 text-white border-green-400"
                    : activeStep === step.id
                    ? "bg-[#EB6AB5]/20 border-[#EB6AB5] text-[#EB6AB5]"
                    : "border-[#EB6AB5]/40 text-[#EB6AB5]/70"
                }`}
              >
                {completed.includes(step.id) ? <CheckCircle size={18} /> : step.icon}
              </div>

              {/* Step Content */}
              <div className="text-left">
                <h4
                  className={`font-semibold transition ${
                    activeStep === step.id
                      ? "text-[#EB6AB5]"
                      : "text-[#EB6AB5]/80 group-hover:text-[#ff83d1]"
                  }`}
                >
                  {step.title}
                </h4>

                {step.id === 1 && (
                  <p className="text-[#EB6AB5]/70 text-sm mt-1">
                    Answer a few quick questions to understand your strengths.
                  </p>
                )}
                {step.id === 2 && (
                  <p className="text-[#EB6AB5]/70 text-sm mt-1">
                    Tell us your work style, goals, and growth aspirations.
                  </p>
                )}
                {step.id === 3 && (
                  <p className="text-[#EB6AB5]/70 text-sm mt-1">
                    Allow AI to analyze your profile securely & privately.
                  </p>
                )}
              </div>
            </div>

            {/* Inline Questionnaire */}
            {step.id === 1 && questionnaireVisible && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="ml-14 mt-3 p-4 border border-[#EB6AB5]/30 rounded-xl bg-[#1c1c2e]/60 text-sm space-y-3"
              >
                <div>
                  <label className="block text-[#EB6AB5]/80 mb-1 text-xs">Age Group</label>
                  <input
                    type="text"
                    value={answers.ageGroup || ""}
                    onChange={(e) => handleAnswerChange("ageGroup", e.target.value)}
                    placeholder="Enter your age group"
                    className="w-full bg-[#11111f] p-2 rounded-md border border-[#EB6AB5]/20 text-white focus:border-[#EB6AB5] outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[#EB6AB5]/80 mb-1 text-xs">Gender</label>
                  <input
                    type="text"
                    value={answers.gender || ""}
                    onChange={(e) => handleAnswerChange("gender", e.target.value)}
                    placeholder="Enter gender"
                    className="w-full bg-[#11111f] p-2 rounded-md border border-[#EB6AB5]/20 text-white focus:border-[#EB6AB5] outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[#EB6AB5]/80 mb-1 text-xs">City / Country</label>
                  <input
                    type="text"
                    value={answers.city || ""}
                    onChange={(e) => handleAnswerChange("city", e.target.value)}
                    placeholder="Enter city or country"
                    className="w-full bg-[#11111f] p-2 rounded-md border border-[#EB6AB5]/20 text-white focus:border-[#EB6AB5] outline-none"
                  />
                </div>

                <button
                  onClick={handleSubmitQuestionnaire}
                  className="mt-3 w-full bg-[#EB6AB5] hover:bg-[#ff83d1] transition px-4 py-2 rounded-full text-white font-semibold text-xs"
                >
                  Submit Questionnaire →
                </button>
              </motion.div>
            )}

            {/* Unlock AI Results (final section) */}
            {step.id === 4 && (
              <div className="space-y-3 mt-3 ml-14">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="border border-[#EB6AB5]/40 rounded-xl p-3"
                >
                  <p className="text-[#EB6AB5] font-semibold text-sm mb-1">
                    ✨ Personalized Strengths Summary
                  </p>
                  <p className="text-[#EB6AB5]/70 text-xs">
                    Boosting your confidence with key strengths & career insights.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="border border-[#EB6AB5]/40 rounded-xl p-3"
                >
                  <p className="text-[#EB6AB5] font-semibold text-sm mb-1">
                    🪄 Tailored Resume Phrasing
                  </p>
                  <p className="text-[#EB6AB5]/70 text-xs">
                    Putting your strongest traits in the best possible terms.
                  </p>
                </motion.div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bottom Button */}
      <div className="flex justify-center mt-10">
        {activeStep < steps.length ? (
          <button
            onClick={() => handleStepClick(activeStep + 1)}
            className="bg-[#EB6AB5] hover:bg-[#ff83d1] transition px-6 py-2 rounded-full text-white font-semibold text-sm"
          >
            Continue →
          </button>
        ) : (
          <button className="bg-green-600 hover:bg-green-500 transition px-6 py-2 rounded-full text-white font-semibold text-sm">
            Finished ✅
          </button>
        )}
      </div>
    </div>
  );
}
